import React, { useState, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  TextField, 
  Button, 
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Card,
  CardContent,
  Snackbar,
  Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import emailjs from '@emailjs/browser';
import CalendlyBooking from '../components/CalendlyBooking';

const ContactSection = () => {
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const formRef = useRef();
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validate = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!formValues.name) errors.name = 'Name is required';
    if (!formValues.email) errors.email = 'Email is required';
    else if (!emailRegex.test(formValues.email)) errors.email = 'Email format is invalid';
    if (!formValues.message) errors.message = 'Message is required';
    
    return errors;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setLoading(true);
    
    // EmailJS configuration
    // You'll need to replace these IDs with your actual EmailJS account details
    const serviceID = 'service_poj6v76'; // replace with your service ID
    const templateID = 'template_4l8prdc'; // replace with your template ID
    const publicKey = 'g0fD3rwDM_UElwin6'; // replace with your public key
    
    emailjs.sendForm(serviceID, templateID, formRef.current, publicKey)
      .then((result) => {
        console.log('Email sent successfully:', result.text);
        setFormSubmitted(true);
        setLoading(false);
        
        // Reset form
        setFormValues({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
      }, (error) => {
        console.error('Failed to send email:', error.text);
        setLoading(false);
        setSnackbar({
          open: true,
          message: 'Failed to send message. Please try again later.',
          severity: 'error'
        });
      });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  return (
    <Box 
      sx={{ 
        py: { xs: 8, md: 12 },
        backgroundColor: 'rgba(246, 248, 255, 0.8)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '0',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,145,77,0.08) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '5%',
          left: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(82,113,255,0.08) 0%, rgba(255,255,255,0) 70%)',
          zIndex: 0
        }}
      />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div"
              color="primary"
              fontWeight={600}
              sx={{ mb: 1, letterSpacing: 2 }}
            >
              GET IN TOUCH
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight={700}
              sx={{ mb: 2 }}
            >
              Start Your Project
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                fontSize: '1.1rem'
              }}
            >
              Ready to bring your small business into the digital world? Let's discuss how I can help you connect with more customers and simplify your daily operations.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4,
                  borderRadius: '16px',
                  border: '1px solid rgba(230, 230, 230, 0.5)',
                  backgroundColor: 'white',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}
              >
                {formSubmitted ? (
                  <Box 
                    sx={{ 
                      py: 4, 
                      textAlign: 'center', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '400px'
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                    >
                      <Box 
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          borderRadius: '50%', 
                          bgcolor: 'success.light', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          mb: 3,
                          mx: 'auto'
                        }}
                      >
                        <Box 
                          component="span" 
                          sx={{ 
                            color: 'white', 
                            fontSize: '2.5rem', 
                            fontWeight: 'bold' 
                          }}
                        >
                          ✓
                        </Box>
                      </Box>
                    </motion.div>
                    
                    <Typography variant="h4" fontWeight="bold" mb={2}>
                      Message Sent!
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ mb: 4, maxWidth: '400px', mx: 'auto' }}
                    >
                      Thank you for reaching out. I'll get back to you as soon as possible!
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={() => setFormSubmitted(false)}
                    >
                      Send Another Message
                    </Button>
                  </Box>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Name"
                          name="name"
                          value={formValues.name}
                          onChange={handleChange}
                          error={!!formErrors.name}
                          helperText={formErrors.name}
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formValues.email}
                          onChange={handleChange}
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number (optional)"
                          name="phone"
                          value={formValues.phone}
                          onChange={handleChange}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Business Type</InputLabel>
                          <Select
                            label="Business Type"
                            name="projectType"
                            value={formValues.projectType}
                            onChange={handleChange}
                          >
                            <MenuItem value=""><em>Select a business type</em></MenuItem>
                            <MenuItem value="retail">Retail Shop</MenuItem>
                            <MenuItem value="service">Small Business Owner</MenuItem>
                            <MenuItem value="arts">Artist/Creative</MenuItem>
                            <MenuItem value="wellness">Health & Wellness</MenuItem>
                            <MenuItem value="food">Food & Beverage</MenuItem>
                            <MenuItem value="education">Education & Workshops</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Message"
                          name="message"
                          multiline
                          rows={4}
                          value={formValues.message}
                          onChange={handleChange}
                          error={!!formErrors.message}
                          helperText={formErrors.message}
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button 
                          type="submit" 
                          variant="contained" 
                          color="primary" 
                          size="large"
                          fullWidth
                          disabled={loading}
                          sx={{ 
                            py: 1.5,
                            borderRadius: '30px',
                            fontWeight: 600,
                          }}
                        >
                          {loading ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Paper>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Card 
                  elevation={0}
                  sx={{ 
                    mb: 3,
                    p: 1.5,
                    borderRadius: '16px',
                    border: '1px solid rgba(230, 230, 230, 0.5)',
                    backgroundColor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton 
                      sx={{ 
                        bgcolor: 'rgba(82, 113, 255, 0.1)',
                        color: 'primary.main',
                      }}
                    >
                      <EmailIcon />
                    </IconButton>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight={500}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.main'
                          }
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText('ericrak.dev@gmail.com');
                          setSnackbar({
                            open: true,
                            message: 'Email copied to clipboard!',
                            severity: 'success'
                          });
                        }}
                      >
                        ericrak.dev@gmail.com
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
                
                <Card 
                  elevation={0}
                  sx={{ 
                    p: 1.5,
                    borderRadius: '16px',
                    border: '1px solid rgba(230, 230, 230, 0.5)',
                    backgroundColor: 'white',
                    flexGrow: 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton 
                      sx={{ 
                        bgcolor: 'rgba(108, 212, 166, 0.1)',
                        color: '#6CD4A6',
                      }}
                    >
                      <LocationOnIcon />
                    </IconButton>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Availability
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        Remote Service Provider
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
                
                <Box sx={{ mt: 4, p: 3, borderRadius: '16px', bgcolor: 'primary.main', color: 'white', flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Need a Quick Consultation?
                  </Typography>
                  <Typography variant="body2" mb={3}>
                    Schedule a free 30-minute call to discuss your small business needs and how digital solutions can help you grow locally.
                  </Typography>
                  <CalendlyBooking 
                    buttonProps={{
                      fullWidth: true,
                      sx: {
                        bgcolor: 'white',
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactSection; 